import environments from './XEnvironment.js';
import { XTemplateDOM } from './XDOM.js';
import { xArray, xObject, xReference, xString } from './XTypes.js';
import { xRunSequentialPromises } from './XUtil.js';

import xCommandInjectSite from '../app/XCommand/xCommandInjectSite.js';

import template from './XPageMenuItem.html?raw';

import { Collapse, Offcanvas } from 'bootstrap';

export class XPageMenuItem
{
    static get type () { return 'XPageMenuItem'; }

    #dom;
    #parent;
    #menuRoot;
    #page;
    #site;

    #items;

    #elementMenu;
    #elementMenuItem;

    #isRoot;
    #isMenu;
    #isShown;
    #bsCollapseItem;

    constructor(dom, parent, page, site, menuRoot=null)
    {
        this.#isRoot   = !(parent instanceof XPageMenuItem);
        this.#dom      = dom;
        this.#parent   = parent;
        this.#page     = page;
        this.#site     = site;
        this.#menuRoot = this.#isRoot ? this : menuRoot;

        this.#isMenu = !!this.#dom.menu;
        
        if(this.#isMenu)
        {
            const domTypes = environments.active.domTypes;
            this.#items = this.#dom.menu.items._setCreator(() => model => new domTypes.XPageMenuItem(model, this, this.#page, this.#site, this.#menuRoot));
            this.#elementMenu = this.#dom.menu._element;
            if(this.#isRoot)
            {
                this.#isShown = true;
                this.#dom.menu._element.classList.remove('x-no-animation');
                this.#bsCollapseItem = Offcanvas.getOrCreateInstance(this.#dom._element);
            }
            else
            {
                this.#elementMenuItem = this.#dom.menuItem._element;
                this.#isShown = !this.#elementMenuItem.classList.contains('collapsed');
                this.#bsCollapseItem = new Collapse(this.#elementMenu, {
                    toggle: this.#isShown
                });
                this.#elementMenuItem.addEventListener('click', (event) =>
                {
                    if(this.#isShown)
                    {
                        this.#hide();
                    }
                    else
                    {
                        this.#show();
                    }
                    event.preventDefault();
                });
            }
        }
        else
        {
            this.#elementMenuItem = this.#dom.menuItem._element;
        }
    };

    hideRoot()
    {
        if(!this.#isRoot)
        {
            this.#menuRoot.hideRoot();
            return;
        }
        this.#bsCollapseItem.hide();
    }

    async #hide()
    {
        if(!this.#isMenu || this.#isRoot || this.#bsCollapseItem._isTransitioning || !this.#isShown)
            return;
        this.#elementMenuItem.classList.add('collapsed');
        this.#isShown = false;
        this.#bsCollapseItem.hide();
        return new Promise(resolve => {
            this.#elementMenu.addEventListener('hidden.bs.collapse', () =>
            {
                resolve();
            }, { once: true });
        });
    };
    async #show()
    {
        if(!this.#isMenu || this.#isRoot || this.#bsCollapseItem._isTransitioning || this.#isShown)
            return;
        this.#elementMenuItem.classList.remove('collapsed');
        this.#isShown = true;
        this.#bsCollapseItem.show();
        return new Promise(resolve => {
            this.#elementMenu.addEventListener('shown.bs.collapse', () =>
            {
                resolve();
            }, { once: true });
        });
    };

    #classAnimation(enable)
    {
        this.#menuRoot._dom.menu._element.classList.toggle('x-no-animation', !enable);
    }
    #classHighlight(enable)
    {
        this.#dom.menuItem?._element.classList.toggle('x-highlight', enable);
    }
    #classSemiHighlight(enable)
    {
        this.#dom.menuItem?._element.classList.toggle('x-semi-highlight', enable);
    }

    #unhighlightAll()
    {
        if(this.#isMenu)
        {
            for(const item of this.#items)
            {
                item.#unhighlightAll();
            }
        }
        this.#classHighlight(false);
        this.#classSemiHighlight(false);
    }

    get isHighlighted()
    {
        return this.#elementMenuItem?.classList.contains('x-highlight') ?? false;
    }

    get isSemiHighlighted()
    {
        return this.#elementMenuItem?.classList.contains('x-semi-highlight') ?? false;
    }

    async hideAll(animated=true)
    {
        if(!this.#isMenu || !this.#isShown)
            return;
        if(!animated)
        {
            this.#classAnimation(false);
        }
        for(const item of this.#items)
        {
            item.hideAll();
        }
        this.#hide();
        if(!animated)
        {
            this.#classAnimation(true);
        }
    }

    async highlight(animated=true)
    {
        if(!animated)
        {
            this.#classAnimation(false);
        }
        this.#menuRoot.#unhighlightAll();
        const path = this.path;
        for(let i = 1; i < path.length-1; i++)
        {
            path[i].#classSemiHighlight(true);
        }
        path[path.length-1].#classHighlight(true);
        if(animated)
        {
            await xRunSequentialPromises(path.map(item => () => item.#show()));
        }
        else
        {
            for(const item of path)
            {
                item.#show();
            }
        }
        if(!animated)
        {
            this.#classAnimation(true);
        }
    }

    visit(visitor)
    {
        if(this.#isMenu)
        {
            for(const item of this.#items)
            {
                item.visit(visitor);
            }
        }
        visitor(this);
    }

    set link(url)
    {
        if(this.#dom.menuItem)
        {
            this.#dom.menuItem.label._element.href = url;
        }
    }

    get path()
    {
        return this.#isRoot ? [this] : [...this.#parent.path, this];
    }

    get _dom            () { return this.#dom;             }
    get parent          () { return this.#parent;          }
    get menuRoot        () { return this.#menuRoot;        }
    get page            () { return this.#page;            }
    get site            () { return this.#site;            }
    get items           () { return this.#items;           }
    get elementMenuItem () { return this.#elementMenuItem; }
    get isMenu          () { return this.#isMenu;          }
    get isRoot          () { return this.#isRoot;          }
    get isShown         () { return this.#isShown;         }
};

export class XPageMenuItemReactive extends XPageMenuItem
{
    static get specification () 
    {
        return xObject({
            $: XPageMenuItem.type,
            label: xString('', {
                localized: true
            }),
            link: xString(),
            route: xString(),
            page: xReference('XPage'),
            items: xArray(xReference([XPageMenuItem.type]))
        });
    }

    static #template;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XPageMenuItemReactive.specification, domEnvironment);
    }

    #data;

    constructor(data, parent, page, site, menuRoot=null)
    {
        const templateType = parent instanceof XPageMenuItemReactive ? (data.items.length == 0 ? 'ITEM' : 'MENU') : 'ROOT';
        super(XPageMenuItemReactive.#template[templateType]._createAccessibleDOM(data), parent, page, site, menuRoot);
        this.#data = data;

        this._dom._attachData(this.#data.items_, {
            _emptyStateChanged: () => this._remount()
        }, false);

        if(this.isRoot)
        {
            this._dom.titlePages._bind(this.page.data.site?.titlePages_);
        }

        if(this.site.isEditor)
        {
            this._dom.editor?.addAfter._element.addEventListener('click', (event) => 
            {
                this.addNewAfter();
                event.stopPropagation();
            });
            this._dom.editor?.addInside?._element.addEventListener('click', (event) => 
            {
                this.addNewInside();
                event.stopPropagation();
            });
        }
        else
        {
            //this._dom.menuItem?.edit._element.remove();
            if(this.site.isTranslator)
            {
                this._dom.editor?._destroy();
            }
        }
        this._dom.menuItem?.edit._element.addEventListener('click', (event) => 
        {
            this.page.showSettings(this);
            this.highlight();
            event.stopPropagation();
        });
        this._dom.menuItem?.label._element.addEventListener('click', (event) => 
        {
            if(this.#data.page)
            {
                this.hideRoot();
                this.page.showContent(this);
                this.highlight();
            }
            event.stopPropagation();
            event.preventDefault();
        });
    };

    index()
    {
        return this.parent.items?.indexOf(this);
    }

    remove()
    {
        const indexSibling = Math.min(this.index(), this.parent.items.length-2);
        const indexParent = this.parent.index();
        if(this.parent._removeFromData(this))
        {
            if(this.parent.items.length == 0)
            {
                return this.parent.parent.items[indexParent];
            }
            return this.parent.items[indexSibling];
        }
        return this;
    }

    _removeFromData(pageMenuItem)
    {
        if(this.isRoot && this.items.length == 1)
        {
            return false;
        }
        const index = this.items.indexOf(pageMenuItem);
        if(index > -1)
        {
            this.data.items.splice(index, 1);
            return true;
        }
        return false;
    }

    addNewAfter()
    {
        const index = this.index();
        const pageMenuItem = this.createNewDataWithPage();
        this.parent.data.items.splice(index+1, 0, pageMenuItem._key);
        this.page.showSettings(this.parent.items[index+1]);
    }

    addNewBefore()
    {
        const index = this.index();
        const pageMenuItem = this.createNewDataWithPage();
        this.parent.data.items.splice(index, 0, pageMenuItem._key);
        this.page.showSettings(this.parent.items[index]);
    }

    addNewInside()
    {
        const index = this.index();
        const pageMenuItem = this.createNewDataWithPage();
        this.#data.items.push(pageMenuItem._key);
        // Due to remount, this item might not exist anymore, so we get 
        // the item at the same index from the parent
        const newPageMenuItem = this.parent.items[index];
        this.page.showSettings(newPageMenuItem.items[newPageMenuItem.items.length-1]);
    }

    createNewDataWithPage()
    {
        const page = this.page.createNewData();
        const pageMenuItem = this.createNewData();
        pageMenuItem.page = page._key;
        return pageMenuItem;
    }

    createNewData()
    {
        const pageMenuItem = this.site.storage.addData('XPageMenuItem');
        pageMenuItem.label = 'NEW';
        pageMenuItem.route = 'new';
        return pageMenuItem;
    }

    _render(dom)
    {
        if(this.isRoot)
        {
            dom.menu._element.classList.add('x-no-animation');
        }
        else if(this.isMenu)
        {
            dom.menuItem._element.classList.toggle('collapsed', !(this.isHighlighted || this.isSemiHighlighted));
            dom.menu._element.classList.add('collapse');
            dom.menu._element.classList.remove('collapsing');
            dom.menu._element.classList.remove('show');
        }
    }

    get data () { return this.#data; }
};
